import { useRef, useLayoutEffect, useEffect, useCallback } from 'react'

import { Sort } from '../icons/index.js'

import styles from './RecordTable.module.scss'

/**
 * RecordTable — CSS Subgrid layout primitive for tabular data.
 *
 * Renders a scrollable grid with a sticky header and optional sticky leading
 * columns. Zero domain logic — all presentation decisions belong to the caller
 * (column definitions, row state, sorting, click handling).
 *
 * ## Layout
 *
 * The outer container is a single CSS Grid (`grid-template-columns` derived
 * from `columns[].width`). Every header and data row is `display:grid` +
 * `grid-template-columns:subgrid` so all columns align without a table element.
 *
 * ## Sticky columns
 *
 * After each render `useLayoutEffect` measures the `offsetLeft` of the first
 * `stickyColumns` header cells and stores the values as CSS custom properties
 * (`--rt-sticky-left-{i}`) on the container element. Sticky cells reference
 * those vars via inline `left` style so all rows share the same measured offsets.
 *
 * @param {object}   props
 * @param {Array<ColumnDef>}  props.columns           - Column definitions.
 * @param {Array<object>}     props.rows              - Data rows (any shape).
 * @param {function}          props.getRowKey         - (row) => string|number — React key + aria.
 * @param {function}         [props.getRowState]      - (row) => 'inaccessible'|'sold'|'reserved'|null. Applied as CSS class.
 * @param {string|null}      [props.activeRowKey]     - Key of the currently selected/active row (from getRowKey). Gets rowActive CSS class.
 * @param {function}         [props.onRowClick]       - (row) => void. If provided rows get pointer cursor + hover.
 * @param {function}         [props.onSort]           - (columnId, direction) => void. Called when a sortable header is clicked.
 * @param {string|null}      [props.sortColumnId]     - Id of currently sorted column.
 * @param {'asc'|'desc'}     [props.sortDirection]    - Current sort direction. Default 'asc'.
 * @param {string}           [props.scrollHeight]     - CSS value for `max-height` on the scroll container. Default '75vh'.
 * @param {number}           [props.stickyColumns]    - How many leading columns are sticky. Default 0.
 * @param {string}           [props.noDataText]       - Text shown when rows is empty. Default 'Brak danych'.
 *
 * @typedef {object} ColumnDef
 * @property {string}   id              - Unique column id. Used as `key` and for sort matching.
 * @property {string}   label           - Header cell text.
 * @property {string}   width           - CSS track size (e.g. '120px', '1fr', 'minmax(80px,1fr)').
 * @property {boolean} [sortable]       - Whether clicking the header emits onSort.
 * @property {'left'|'center'|'right'} [align] - Cell content alignment. Default left.
 * @property {function} [render]        - (row) => ReactNode. If omitted, renders `row[id]`.
 */
export function RecordTable({
  columns = [],
  rows = [],
  getRowKey,
  getRowState,
  activeRowKey = null,
  onRowClick,
  onSort,
  sortColumnId = null,
  sortDirection = 'asc',
  scrollHeight = '75vh',
  stickyColumns = 0,
  noDataText = 'Brak danych',
}) {
  const containerRef = useRef(null)
  // Refs for the first N header cells — used to measure offsetLeft for sticky positioning.
  const headerCellRefs = useRef([])

  // Build --rt-columns from column widths.
  const gridTemplateColumns = columns.map(col => col.width ?? 'auto').join(' ')

  // Compute sticky column left offsets after every render.
  //
  // We do NOT measure visual position — that's fragile with CSS Subgrid +
  // position:sticky (sticky makes .header an offsetParent, causing offsetLeft
  // to be measured relative to the header rather than the scroll container).
  //
  // Instead: accumulate widths. Column 0 always sticks at left=0, column 1 at
  // left=width_of_col_0, column 2 at left=width_of_col_0+width_of_col_1, etc.
  // getBoundingClientRect().width gives the rendered (computed) track width.
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container || stickyColumns <= 0) return
    let accumulated = 0
    for (let i = 0; i < stickyColumns; i++) {
      const cell = headerCellRefs.current[i]
      container.style.setProperty(`--rt-sticky-left-${i}`, `${accumulated}px`)
      if (cell) accumulated += cell.getBoundingClientRect().width
    }
  }) // intentionally no dep array — re-measure on every render for correctness

  // Toggle .scrolledX on the container so sticky-column shadows fade in
  // only when the user has actually scrolled horizontally.
  useEffect(() => {
    const el = containerRef.current
    if (!el || stickyColumns <= 0) return
    const onScroll = () => {
      el.classList.toggle(styles.scrolledX, el.scrollLeft > 0)
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [stickyColumns])

  const handleHeaderClick = useCallback(
    col => {
      if (!col.sortable || !onSort) return
      const newDir = sortColumnId === col.id && sortDirection === 'asc' ? 'desc' : 'asc'
      onSort(col.id, newDir)
    },
    [onSort, sortColumnId, sortDirection],
  )

  const containerStyle = {
    '--rt-columns': gridTemplateColumns,
    '--rt-scroll-height': scrollHeight,
  }

  return (
    <div ref={containerRef} className={styles.container} style={containerStyle} role="table">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className={styles.header} role="row">
        {columns.map((col, colIndex) => {
          const isSticky = colIndex < stickyColumns
          const isSorted = sortColumnId === col.id

          const isStickyLast = isSticky && colIndex === stickyColumns - 1
          const isStickyFirst = isSticky && colIndex === 0

          const headerCellClass = [
            styles.headerCell,
            col.sortable             ? styles.headerCellSortable : '',
            isSorted                 ? styles.headerCellSorted   : '',
            isSticky                 ? styles.stickyCell         : '',
            isSticky                 ? styles.headerStickyCell   : '',
            isStickyFirst            ? styles.stickyFirst        : '',
            isStickyLast             ? styles.stickyLast         : '',
            col.align === 'right'    ? styles.cellAlignRight     : '',
            col.align === 'center'   ? styles.cellAlignCenter    : '',
          ].filter(Boolean).join(' ')

          return (
            <div
              key={col.id}
              ref={el => { headerCellRefs.current[colIndex] = el }}
              className={headerCellClass}
              role="columnheader"
              style={isSticky ? { left: `var(--rt-sticky-left-${colIndex}, 0px)` } : undefined}
              onClick={col.sortable ? () => handleHeaderClick(col) : undefined}
              aria-sort={
                col.sortable
                  ? isSorted
                    ? sortDirection === 'asc' ? 'ascending' : 'descending'
                    : 'none'
                  : undefined
              }
            >
              <span className={styles.headerCellLabel}>{col.label}</span>
              {col.sortable && (
                <Sort
                  className={[
                    styles.sortIcon,
                    isSorted                             ? styles.sortIconActive : '',
                    isSorted && sortDirection === 'desc' ? styles.sortIconDesc   : '',
                  ].filter(Boolean).join(' ')}
                />
              )}
            </div>
          )
        })}
      </div>

      {/* ── Rows ────────────────────────────────────────────────────────────── */}
      {rows.length === 0 ? (
        <div className={styles.noData} role="row">
          <span role="cell">{noDataText}</span>
        </div>
      ) : (
        rows.map((row, rowIndex) => {
          const rowKey     = getRowKey(row)
          const rowState   = getRowState ? getRowState(row) : null

          const rowClass = [
            styles.row,
            rowState === 'inaccessible' ? styles.rowInaccessible : '',
            rowState === 'sold'         ? styles.rowSold         : '',
            rowState === 'reserved'     ? styles.rowReserved     : '',
            rowKey    === activeRowKey  ? styles.rowActive       : '',
            onRowClick                  ? styles.rowClickable    : '',
          ].filter(Boolean).join(' ')

          return (
            <div
              key={rowKey}
              data-row-key={rowKey}
              className={rowClass}
              role="row"
              aria-rowindex={rowIndex + 1}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((col, colIndex) => {
                const isSticky     = colIndex < stickyColumns
                const isStickyLast = isSticky && colIndex === stickyColumns - 1
                const isStickyFirst = isSticky && colIndex === 0

                const cellClass = [
                  styles.cell,
                  isSticky               ? styles.stickyCell      : '',
                  isStickyFirst          ? styles.stickyFirst     : '',
                  isStickyLast           ? styles.stickyLast      : '',
                  col.align === 'right'  ? styles.cellAlignRight  : '',
                  col.align === 'center' ? styles.cellAlignCenter : '',
                ].filter(Boolean).join(' ')

                return (
                  <div
                    key={col.id}
                    className={cellClass}
                    role="cell"
                    style={isSticky ? { left: `var(--rt-sticky-left-${colIndex}, 0px)` } : undefined}
                  >
                    {col.render ? col.render(row) : (row[col.id] ?? null)}
                  </div>
                )
              })}
            </div>
          )
        })
      )}
    </div>
  )
}
